import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Button, Modal, Labelbox } from "../";
import {
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Empty from "antd/es/empty";
import { DOCUMENT_ACTION, CLIENT_ACTION } from "../../redux/action";
import { Toaster, dateFormatter, dateTimeFormatter } from "../../utils/common";
import {
  openDocumentInNewTab,
  openDocumentPreviewTab,
} from "../../utils/openDocumentInNewTab";
import { getClientBusinesses } from "../../utils/clientBusinessAccess";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_MAX_MB,
  DOCUMENT_UPLOAD_SIZE_NOTE,
  filterValidDocumentUploadFiles,
  hasInvalidDocumentUploadFiles,
} from "../../utils/documentUploadValidation";
import {
  getUploadedFileNames,
  getRequestedFileCategory,
  getRequestedFileBusinessName,
  normalizeRequestedFileCategory,
  formatReminderFrequencyLabel,
  formatNextReminderLabel,
  getReminderProgressTitle,
  getReminderFrequencyDays,
  DEFAULT_REMINDER_TOTAL_COUNT,
  computeNextReminderAtAfterReset,
} from "../../utils/requestedFilesDisplay";
import { getRootUploadFolderDropdownOptions } from "../../utils/moveDocumentScope";
import {
  appendBusinessIdToFormData,
  withBusinessDocumentParams,
} from "../../utils/businessDocumentContext";
import "./requestedFilesBlock.scss";

const getRowId = (row) =>
  row.requested_file_id ?? row.id ?? row.client_requested_file_id;

const getRowSelectionId = (row, rowIndex = 0) => {
  const id = getRowId(row);
  if (id != null && id !== "") return String(id);
  return `row-${rowIndex}`;
};

/** Default reminder interval (days) when creating a new request. */
const DEFAULT_REMINDER_FREQUENCY_DAYS = "7";

const createEmptyRequestFileRow = () => ({
  key: `req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  value: "",
  files: [],
  templateId: "",
});

const STAFF_ATTACHMENT_MAX_FILES = 10;

/**
 * layoutMode:
 * - hidden: render nothing
 * - buttonOnly: staff — Request files button + modal (Requested from client tab toolbar)
 * - tableOnly: requested files table only (client tab or staff table section)
 * - tableAndButton: staff — button + table on Requested from client tab
 */
const RequestedFilesBlock = ({
  clientId,
  layoutMode = "hidden",
  refreshKey = 0,
  /** "individual" | "business" — filters list and tags new requests (omit for all). */
  requestedFileCategory,
  /** Required when requestedFileCategory is business (per-business segregation). */
  clientBusinessId = null,
  /** Fired after a successful request submit (use to refresh a sibling table instance). */
  onRequestSuccess,
  /** When set, clicking a data row (except the status control) runs this — e.g. navigate to Documents. */
  onRowClick,
  /** Show Business column (client dashboard with multiple businesses). */
  showBusinessNameColumn = false,
  /** Render Request files button without block toolbar wrapper (for documents tabs row). */
  inlineToolbar = false,
}) => {
  const dispatch = useDispatch();
  const categoryFilter = requestedFileCategory
    ? normalizeRequestedFileCategory(requestedFileCategory)
    : null;

  const businessScopeParams = useMemo(
    () =>
      withBusinessDocumentParams(
        {},
        {
          documentCategory: categoryFilter,
          clientBusinessId,
        },
      ),
    [categoryFilter, clientBusinessId],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientBusinesses, setClientBusinesses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestFileRows, setRequestFileRows] = useState([
    createEmptyRequestFileRow(),
  ]);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [reminderFrequencyDays, setReminderFrequencyDays] = useState(
    DEFAULT_REMINDER_FREQUENCY_DAYS,
  );
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [statusMenu, setStatusMenu] = useState({ anchor: null, row: null });
  const [editingRow, setEditingRow] = useState(null);
  const [uploadFlow, setUploadFlow] = useState({
    open: false,
    row: null,
    navPath: [{ id: 0, name: "Home", created_by: 0 }],
    folders: [],
    loading: false,
    selectedFiles: [],
    fileData: [],
    uploading: false,
  });

  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkFrequencyModalOpen, setBulkFrequencyModalOpen] = useState(false);
  const [bulkReminderFrequencyDays, setBulkReminderFrequencyDays] = useState(
    DEFAULT_REMINDER_FREQUENCY_DAYS,
  );
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const showTable =
    layoutMode === "tableOnly" || layoutMode === "tableAndButton";
  const showRequestButton =
    layoutMode === "buttonOnly" || layoutMode === "tableAndButton";
  const isClient = localStorage.getItem("user_role") === "client";
  const showReminderColumns = !isClient;
  const isClientSentFolder = useCallback(
    (folder) => {
      if (!isClient) return true;
      if (!folder) return false;
      if (folder.folder_type) return folder.folder_type === "client";
      // Fallback: if folder_type is missing, treat folders created by client as "sent"
      return String(folder.created_by) === String(clientId);
    },
    [isClient, clientId],
  );

  const filterRowsByCategory = useCallback(
    (list) => {
      if (!categoryFilter || !Array.isArray(list)) return list || [];
      return list.filter(
        (row) =>
          getRequestedFileCategory(row, categoryFilter) === categoryFilter,
      );
    },
    [categoryFilter],
  );

  const normalizeSelectionId = (rowId) => {
    if (rowId == null || rowId === "") return null;
    return String(rowId);
  };

  const applyLoadedRows = useCallback(
    (data) => {
      const nextRows = filterRowsByCategory(Array.isArray(data) ? data : []);
      setRows(nextRows);
      return nextRows;
    },
    [filterRowsByCategory],
  );

  const load = useCallback(async () => {
    if (!clientId || !showTable) {
      if (!showTable) {
        setRows([]);
        setSelectedRowIds([]);
      }
      return;
    }
    setLoading(true);
    try {
      const data = await DOCUMENT_ACTION.getRequestedFiles({
        client_id: clientId,
        ...(categoryFilter ? { requested_file_category: categoryFilter } : {}),
        ...businessScopeParams,
      });
      applyLoadedRows(data);
    } finally {
      setLoading(false);
    }
  }, [
    clientId,
    showTable,
    categoryFilter,
    applyLoadedRows,
    businessScopeParams,
  ]);

  /** Refresh this block's table and/or notify parent (single GET when this instance shows the table). */
  const refreshAfterMutation = useCallback(async () => {
    if (showTable && clientId) {
      setLoading(true);
      try {
        const data = await DOCUMENT_ACTION.getRequestedFiles({
          client_id: clientId,
          ...(categoryFilter
            ? { requested_file_category: categoryFilter }
            : {}),
          ...businessScopeParams,
        });
        applyLoadedRows(data);
      } finally {
        setLoading(false);
      }
    }
    onRequestSuccess?.();
  }, [
    clientId,
    showTable,
    categoryFilter,
    applyLoadedRows,
    onRequestSuccess,
    businessScopeParams,
  ]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const selectableRowEntries = useMemo(
    () =>
      rows
        .map((row, rowIndex) => ({
          row,
          rowIndex,
          selectionId: getRowSelectionId(row, rowIndex),
        }))
        .filter(
          ({ row }) =>
            String(row.status || "pending").toLowerCase() !== "completed",
        ),
    [rows],
  );

  const selectedRows = useMemo(
    () =>
      selectableRowEntries
        .filter((entry) => selectedRowIds.includes(entry.selectionId))
        .map((entry) => entry.row),
    [selectableRowEntries, selectedRowIds],
  );

  const setRowSelection = useCallback((selectionId, checked) => {
    const id = normalizeSelectionId(selectionId);
    if (!id) return;
    setSelectedRowIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((item) => item !== id);
    });
  }, []);

  const allRowSelectionIds = useMemo(
    () => selectableRowEntries.map((entry) => entry.selectionId),
    [selectableRowEntries],
  );

  const toggleSelectAllRows = useCallback(() => {
    setSelectedRowIds((prev) => {
      if (!allRowSelectionIds.length) return prev;
      const hasSelection = allRowSelectionIds.some((id) => prev.includes(id));
      return hasSelection ? [] : allRowSelectionIds;
    });
  }, [allRowSelectionIds]);

  useEffect(() => {
    if (!showBusinessNameColumn || !clientId) {
      setClientBusinesses([]);
      return;
    }

    try {
      const stored = localStorage.getItem("USER_DATA");
      if (stored) {
        const fromStorage = getClientBusinesses(JSON.parse(stored));
        if (fromStorage.length) {
          setClientBusinesses(fromStorage);
        }
      }
    } catch {
      /* ignore */
    }

    let cancelled = false;
    (async () => {
      try {
        const apiList = await CLIENT_ACTION.getClientBusinesses(clientId);
        if (cancelled) return;
        setClientBusinesses(Array.isArray(apiList) ? apiList : []);
      } catch {
        if (!cancelled) setClientBusinesses([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showBusinessNameColumn, clientId]);

  if (layoutMode === "hidden") return null;

  const resetRequestModal = () => {
    setRequestFileRows([createEmptyRequestFileRow()]);
    setReminderFrequencyDays(DEFAULT_REMINDER_FREQUENCY_DAYS);
    setEditingRow(null);
  };

  const openRequestModal = () => {
    resetRequestModal();
    setModalOpen(true);
    const category = categoryFilter || "individual";
    DOCUMENT_ACTION.listDocumentTemplates(category).then((res) => {
      setDocumentTemplates(
        res?.status === 1 && Array.isArray(res.data) ? res.data : [],
      );
    });
  };

  const closeRequestModal = () => {
    setModalOpen(false);
    resetRequestModal();
  };

  const addRequestFileRow = () => {
    setRequestFileRows((prev) => [...prev, createEmptyRequestFileRow()]);
  };

  const removeRequestFileRow = (key) => {
    setRequestFileRows((prev) => {
      if (prev.length <= 1) {
        return [{ ...prev[0], value: "", files: [], templateId: "" }];
      }
      return prev.filter((row) => row.key !== key);
    });
  };

  const updateRequestFileRow = (key, value) => {
    setRequestFileRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, value } : row)),
    );
  };

  const updateRequestFileTemplate = (key, templateId) => {
    const template = documentTemplates.find(
      (item) => String(item.template_id) === String(templateId),
    );
    setRequestFileRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, templateId: templateId || "" };
        if (template && !String(row.value || "").trim()) {
          next.value = template.template_name;
        }
        return next;
      }),
    );
  };

  const addRequestFileAttachments = (key, fileList) => {
    const { validFiles, errors } = filterValidDocumentUploadFiles(fileList);
    if (errors.length) Toaster.error(errors[0]);
    if (!validFiles.length) return;
    setRequestFileRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const next = [...(row.files || [])];
        validFiles.forEach((file) => {
          const exists = next.some(
            (item) => item.name === file.name && item.size === file.size,
          );
          if (!exists && next.length < STAFF_ATTACHMENT_MAX_FILES) {
            next.push(file);
          }
        });
        if ((row.files || []).length + validFiles.length > STAFF_ATTACHMENT_MAX_FILES) {
          Toaster.warning(
            `You can attach up to ${STAFF_ATTACHMENT_MAX_FILES} documents per file name.`,
          );
        }
        return { ...row, files: next };
      }),
    );
  };

  const removeRequestFileAttachment = (key, fileIndex) => {
    setRequestFileRows((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              files: (row.files || []).filter((_, index) => index !== fileIndex),
            }
          : row,
      ),
    );
  };

  const downloadStaffAttachment = async (row, attachment) => {
    try {
      await DOCUMENT_ACTION.downloadRequestedFileAttachment({
        requested_file_id: getRowId(row),
        index: attachment.index,
        name: attachment.name,
        contentType: attachment.content_type,
      });
    } catch (error) {
      Toaster.error(error?.message || "Could not download document");
    }
  };

  const previewStaffAttachment = async (row, attachment) => {
    const previewTab = openDocumentPreviewTab();
    try {
      const result = await DOCUMENT_ACTION.downloadRequestedFileAttachment({
        requested_file_id: getRowId(row),
        index: attachment.index,
        name: attachment.name,
        contentType: attachment.content_type,
        preview: true,
      });
      await openDocumentInNewTab(
        result?.blobUrl,
        result?.documentType || attachment.content_type,
        result?.documentName || attachment.name,
        previewTab,
      );
    } catch (error) {
      if (previewTab && !previewTab.closed) previewTab.close();
      Toaster.error(error?.message || "Could not preview document");
    }
  };

  const submitRequest = async () => {
    const namedRows = requestFileRows
      .map((row) => ({
        name: String(row.value ?? "").trim(),
        files: row.files || [],
        templateId: row.templateId || "",
      }))
      .filter((row) => row.name);
    const uniqueRows = [];
    const seenNames = new Set();
    namedRows.forEach((row) => {
      const key = row.name.toLowerCase();
      const existing = uniqueRows.find(
        (item) => item.name.toLowerCase() === key,
      );
      if (existing) {
        existing.files = [...existing.files, ...row.files].slice(
          0,
          STAFF_ATTACHMENT_MAX_FILES,
        );
        if (!existing.templateId && row.templateId) {
          existing.templateId = row.templateId;
        }
        return;
      }
      if (seenNames.has(key)) return;
      seenNames.add(key);
      uniqueRows.push({
        name: row.name,
        files: [...row.files],
        templateId: row.templateId || "",
      });
    });
    const uniqueNames = uniqueRows.map((row) => row.name);
    const attachments = uniqueRows.map((row) => row.files);
    const templateIds = uniqueRows.map((row) => row.templateId || "");

    if (!uniqueNames.length) {
      Toaster.warning("Please enter at least one file name");
      return;
    }

    const frequencyDays = Number(reminderFrequencyDays);
    if (!Number.isInteger(frequencyDays) || frequencyDays <= 0) {
      Toaster.warning(
        "Please enter reminder frequency in days (positive number)",
      );
      return;
    }

    if (!clientId) return;

    setSubmitting(true);
    try {
      if (editingRow) {
        const rid = getRowId(editingRow);
        const { updatePayload, frequencyChanged, nextReminderAt } =
          buildFrequencyUpdatePayload(editingRow, frequencyDays);
        updatePayload.file_name = uniqueNames[0];

        const res = await DOCUMENT_ACTION.updateRequestedFileDetails(
          updatePayload,
        );
        if (res && res.status === 1) {
          if (frequencyChanged) {
            // Optimistic UI so Next reminder / progress update before refetch.
            setRows((prev) =>
              prev.map((row) => {
                if (String(getRowId(row)) !== String(rid)) return row;
                return {
                  ...row,
                  file_name: uniqueNames[0],
                  reminder_frequency_days: frequencyDays,
                  reminder_total_count: DEFAULT_REMINDER_TOTAL_COUNT,
                  reminders_sent_count: 1,
                  reminders_completed: false,
                  next_reminder_at: nextReminderAt,
                  remindersSentCount: 1,
                  remindersCompleted: false,
                  reminderTotalCount: DEFAULT_REMINDER_TOTAL_COUNT,
                  nextReminderAt: nextReminderAt,
                };
              }),
            );
          }
          if (uniqueNames.length > 1) {
            await DOCUMENT_ACTION.requestFilesFromClient(
              withBusinessDocumentParams(
                {
                  client_id: clientId,
                  file_names: uniqueNames.slice(1),
                  requested_file_category: categoryFilter || "individual",
                  reminder_frequency_days: frequencyDays,
                  reminder_total_count: DEFAULT_REMINDER_TOTAL_COUNT,
                },
                {
                  documentCategory: categoryFilter,
                  clientBusinessId,
                },
              ),
            );
          }
          Toaster.success("File request updated successfully");
          closeRequestModal();
          await refreshAfterMutation();
        } else {
          Toaster.error(res?.message || "Could not update request");
        }
      } else {
        const res = await DOCUMENT_ACTION.requestFilesFromClient(
          withBusinessDocumentParams(
            {
              client_id: clientId,
              file_names: uniqueNames,
              attachments,
              template_ids: templateIds,
              requested_file_category: categoryFilter || "individual",
              reminder_frequency_days: frequencyDays,
              reminder_total_count: DEFAULT_REMINDER_TOTAL_COUNT,
            },
            {
              documentCategory: categoryFilter,
              clientBusinessId,
            },
          ),
        );
        if (res && res.status === 1) {
          Toaster.success(res.message || "File request(s) sent");
          closeRequestModal();
          await refreshAfterMutation();
        } else {
          Toaster.error(res?.message || "Could not send request");
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onStatusChange = async (row, nextStatus) => {
    const id = getRowId(row);
    if (
      !id ||
      String(row.status).toLowerCase() === String(nextStatus).toLowerCase()
    )
      return;
    setUpdatingId(id);
    try {
      const res = await DOCUMENT_ACTION.updateRequestedFileStatus({
        requested_file_id: id,
        status: nextStatus,
      });
      if (res && res.status === 1) {
        Toaster.success(res.message || "Status updated");
        await refreshAfterMutation();
      } else {
        Toaster.error(res?.message || "Could not update status");
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const fileNameCell = (row) =>
    row.file_name ?? row.document_name ?? row.name ?? "—";

  const buildFrequencyUpdatePayload = (row, frequencyDays) => {
    const rid = getRowId(row);
    const prevFrequency = getReminderFrequencyDays(row);
    const frequencyChanged = prevFrequency !== frequencyDays;
    const nextReminderAt = frequencyChanged
      ? computeNextReminderAtAfterReset(frequencyDays)
      : null;

    const updatePayload = {
      requested_file_id: rid,
      file_name: fileNameCell(row),
      reminder_frequency_days: frequencyDays,
    };

    if (frequencyChanged) {
      updatePayload.reset_reminder_schedule = true;
      updatePayload.send_immediate_reminder = true;
      updatePayload.reminder_total_count = DEFAULT_REMINDER_TOTAL_COUNT;
      updatePayload.reminders_sent_count = 1;
      updatePayload.reminders_completed = false;
      updatePayload.next_reminder_at = nextReminderAt;
    }

    return { updatePayload, frequencyChanged, nextReminderAt, rid };
  };

  const allRowsSelected =
    allRowSelectionIds.length > 0 &&
    allRowSelectionIds.every((id) => selectedRowIds.includes(id));

  const someRowsSelected =
    selectedRowIds.some((id) => allRowSelectionIds.includes(id)) &&
    !allRowsSelected;

  const openBulkFrequencyModal = () => {
    if (!selectedRows.length) {
      Toaster.warning("Select at least one requested file");
      return;
    }
    setBulkReminderFrequencyDays(DEFAULT_REMINDER_FREQUENCY_DAYS);
    setBulkFrequencyModalOpen(true);
  };

  const closeBulkFrequencyModal = () => {
    setBulkFrequencyModalOpen(false);
    setBulkReminderFrequencyDays(DEFAULT_REMINDER_FREQUENCY_DAYS);
  };

  const submitBulkFrequencyChange = async () => {
    if (!selectedRows.length) {
      Toaster.warning("Select at least one requested file");
      return;
    }

    const frequencyDays = Number(bulkReminderFrequencyDays);
    if (!Number.isInteger(frequencyDays) || frequencyDays <= 0) {
      Toaster.warning(
        "Please enter reminder frequency in days (positive number)",
      );
      return;
    }

    setBulkUpdating(true);
    try {
      const updates = selectedRows.map(
        (row) => buildFrequencyUpdatePayload(row, frequencyDays).updatePayload,
      );
      const res = await DOCUMENT_ACTION.bulkUpdateRequestedFileDetails({
        updates,
      });

      const updatedCount =
        Number(res?.data?.updated_count) ||
        (res?.status === 1 ? updates.length : 0);

      if (updatedCount > 0) {
        const updatedIdSet = new Set(
          selectedRows.map((row) => String(getRowId(row))),
        );
        setRows((prev) =>
          prev.map((row) => {
            if (!updatedIdSet.has(String(getRowId(row)))) return row;
            const { frequencyChanged, nextReminderAt } =
              buildFrequencyUpdatePayload(row, frequencyDays);
            if (!frequencyChanged) {
              return { ...row, reminder_frequency_days: frequencyDays };
            }
            return {
              ...row,
              reminder_frequency_days: frequencyDays,
              reminder_total_count: DEFAULT_REMINDER_TOTAL_COUNT,
              reminders_sent_count: 1,
              reminders_completed: false,
              next_reminder_at: nextReminderAt,
              remindersSentCount: 1,
              remindersCompleted: false,
              reminderTotalCount: DEFAULT_REMINDER_TOTAL_COUNT,
              nextReminderAt: nextReminderAt,
            };
          }),
        );
      }

      if (res && res.status === 1) {
        Toaster.success(
          res.message ||
            `Reminder frequency updated for ${updatedCount} file${
              updatedCount === 1 ? "" : "s"
            }`,
        );
        closeBulkFrequencyModal();
        setSelectedRowIds([]);
        await refreshAfterMutation();
      } else if (updatedCount > 0) {
        Toaster.warning(
          res?.message ||
            `Updated ${updatedCount} of ${updates.length} file${
              updates.length === 1 ? "" : "s"
            }`,
        );
        await refreshAfterMutation();
      } else {
        Toaster.error(res?.message || "Could not update reminder frequency");
      }
    } finally {
      setBulkUpdating(false);
    }
  };

  const closeStatusMenu = () => setStatusMenu({ anchor: null, row: null });

  const openStatusMenu = (event, row) => {
    event.stopPropagation();
    setStatusMenu({ anchor: event.currentTarget, row });
  };

  const pickStatus = async (nextStatus) => {
    const row = statusMenu.row;
    closeStatusMenu();
    if (row) await onStatusChange(row, nextStatus);
  };

  const openUploadFlow = async (row) => {
    if (!clientId) return;
    const st = String(row?.status || "pending").toLowerCase();
    if (st === "completed") {
      return;
    }
    setUploadFlow((prev) => ({
      ...prev,
      open: true,
      row,
      navPath: [{ id: 0, name: "Home", created_by: 0 }],
      folders: [],
      loading: true,
      selectedFiles: [],
      fileData: [],
      uploading: false,
    }));
    const uploadCategory = getRequestedFileCategory(row, categoryFilter);
    const uploadBusinessId = row?.client_business_id || clientBusinessId;
    try {
      const homeFolder = localStorage.getItem("user_root_folder") || "Home";
      const response = await dispatch(
        DOCUMENT_ACTION.getUserFolders(
          withBusinessDocumentParams(
            {
              user_id: clientId,
              folder_name: homeFolder,
              folder_id: 0,
              document_category: uploadCategory,
            },
            {
              documentCategory: uploadCategory,
              clientBusinessId: uploadBusinessId,
            },
          ),
        ),
      );
      const folderList = Array.isArray(response?.payload)
        ? response.payload
        : [];
      setUploadFlow((prev) => ({
        ...prev,
        folders: folderList.filter(isClientSentFolder),
        loading: false,
      }));
    } catch (e) {
      setUploadFlow((prev) => ({ ...prev, folders: [], loading: false }));
    }
  };

  const closeUploadFlow = () => {
    setUploadFlow({
      open: false,
      row: null,
      navPath: [{ id: 0, name: "Home", created_by: 0 }],
      folders: [],
      loading: false,
      selectedFiles: [],
      fileData: [],
      uploading: false,
    });
  };

  const getUploadHomeFolder = () =>
    localStorage.getItem("user_root_folder") || "Home";

  const getUploadPath = () => {
    const last = uploadFlow.navPath[uploadFlow.navPath.length - 1];
    if (last.name === "Home") {
      return { id: 0, name: getUploadHomeFolder(), created_by: 0 };
    }
    return last;
  };

  const getUploadKeyPath = () => {
    const parts = uploadFlow.navPath.map((p) => p.name).filter(Boolean);
    if (!parts.length) return getUploadHomeFolder();
    const mapped = [...parts];
    mapped[0] = getUploadHomeFolder();
    return mapped.join("/");
  };

  const hasSelectedUploadFolder = uploadFlow.navPath.length > 1;

  const fetchFoldersForUploadPath = async (nextNavPath) => {
    if (!clientId) return;
    const last = nextNavPath[nextNavPath.length - 1];
    const folderName = last.name === "Home" ? getUploadHomeFolder() : last.name;
    const folderId = last.id;
    const uploadCategory = getRequestedFileCategory(
      uploadFlow.row,
      categoryFilter,
    );
    const uploadBusinessId =
      uploadFlow.row?.client_business_id || clientBusinessId;
    setUploadFlow((prev) => ({ ...prev, loading: true }));
    try {
      const response = await dispatch(
        DOCUMENT_ACTION.getUserFolders(
          withBusinessDocumentParams(
            {
              user_id: clientId,
              folder_name: folderName,
              folder_id: folderId,
              document_category: uploadCategory,
            },
            {
              documentCategory: uploadCategory,
              clientBusinessId: uploadBusinessId,
            },
          ),
        ),
      );
      const folderList = Array.isArray(response?.payload)
        ? response.payload
        : [];
      setUploadFlow((prev) => ({
        ...prev,
        navPath: nextNavPath,
        folders: folderList.filter(isClientSentFolder),
        loading: false,
      }));
    } catch (e) {
      setUploadFlow((prev) => ({
        ...prev,
        navPath: nextNavPath,
        folders: [],
        loading: false,
      }));
    }
  };

  const openFolderInUploadPicker = (folder) => {
    const next = [
      ...uploadFlow.navPath,
      {
        id: folder.client_folder_id,
        name: folder.folder_name,
        created_by: folder.created_by || 0,
        folder_type: folder.folder_type,
      },
    ];
    fetchFoldersForUploadPath(next);
  };

  const goToCrumbInUploadPicker = (index) => {
    const next = uploadFlow.navPath.slice(0, index + 1);
    fetchFoldersForUploadPath(next);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      Toaster.warning("Please enter or select a folder name");
      return;
    }
    setCreatingFolder(true);
    try {
      const uploadCategory = getRequestedFileCategory(
        uploadFlow.row,
        categoryFilter,
      );
      const uploadBusinessId =
        uploadFlow.row?.client_business_id || clientBusinessId;
      const path = getUploadPath();
      const response = await DOCUMENT_ACTION.createFolder(
        withBusinessDocumentParams(
          {
            user_id: clientId,
            folderName: newFolderName.trim(),
            parentFolderName: path.name,
            parentFolderId: path.id,
            folder_type: "client",
            path: getUploadKeyPath(),
            home_folder: getUploadHomeFolder(),
            directory_details: JSON.stringify(uploadFlow.navPath),
            document_category: uploadCategory,
          },
          {
            documentCategory: uploadCategory,
            clientBusinessId: uploadBusinessId,
          },
        ),
      );

      if (response.status === 200 && response.data.status === 1) {
        Toaster.success("Folder created successfully");
        setAddFolderOpen(false);
        setNewFolderName("");
        await fetchFoldersForUploadPath(uploadFlow.navPath);
      } else {
        Toaster.error(
          response?.data?.message || "Error occurred while creating the folder",
        );
      }
    } catch (err) {
      Toaster.error("Error occurred while creating the folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  const onPickFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const { validFiles, errors } = filterValidDocumentUploadFiles(files);
    errors.forEach((message) => Toaster.error(message));
    if (!validFiles.length) return;
    setUploadFlow((prev) => ({
      ...prev,
      selectedFiles: [...prev.selectedFiles, ...validFiles],
      fileData: [...prev.fileData, ...validFiles],
    }));
  };

  const removePickedFile = (idx) => {
    setUploadFlow((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== idx),
      fileData: prev.fileData.filter((_, i) => i !== idx),
    }));
  };

  const buildUploadFormDataForRequest = (files, requestedFileId) => {
    const path = getUploadPath();
    const formData = new FormData();
    (files || []).forEach((file) => formData.append("file", file));
    formData.append("path", getUploadKeyPath());
    formData.append("home_folder", getUploadHomeFolder());
    formData.append("directory_details", JSON.stringify(uploadFlow.navPath));
    formData.append("folder_name", path.name);
    formData.append("folder_id", path.id);
    formData.append("doc_type", "client");
    formData.append("user_id", clientId);
    formData.append("requested_file_id", requestedFileId);

    const USER_DATA = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
    formData.append("send_email", true);
    formData.append("to_email", USER_DATA?.employee_email || "");
    formData.append("first_name", USER_DATA?.employee || "");
    formData.append("user_role", "client");
    const uploadCategory = getRequestedFileCategory(
      uploadFlow.row,
      categoryFilter,
    );
    const uploadBusinessId =
      uploadFlow.row?.client_business_id || clientBusinessId;
    formData.append("document_category", uploadCategory);
    appendBusinessIdToFormData(formData, {
      documentCategory: uploadCategory,
      clientBusinessId: uploadBusinessId,
    });
    return formData;
  };

  const submitUploadForRequest = async () => {
    if (!clientId) return;
    if (!uploadFlow.row) return;
    if (!hasSelectedUploadFolder) {
      Toaster.warning("Please select a folder first");
      return;
    }
    if (!uploadFlow.fileData.length) {
      Toaster.warning("Please choose at least one file to upload");
      return;
    }
    if (hasInvalidDocumentUploadFiles(uploadFlow.fileData)) {
      Toaster.error(
        `One or more files exceed ${DOCUMENT_UPLOAD_MAX_MB} MB or use a blocked file type.`,
      );
      return;
    }
    const requestedFileId = getRowId(uploadFlow.row);
    if (!requestedFileId) return;

    setUploadFlow((prev) => ({ ...prev, uploading: true }));
    try {
      // One FormData / one uploadfile call; backend links all files to the request.
      const formData = buildUploadFormDataForRequest(
        uploadFlow.fileData,
        requestedFileId,
      );
      const response = await DOCUMENT_ACTION.uploadFile({ formData });

      if (response?.status === 200 && response?.data?.status === 1) {
        const count = uploadFlow.fileData.length;
        Toaster.success(
          response?.data?.message ||
            (count === 1
              ? "File uploaded for this request"
              : `${count} files uploaded for this request`),
        );
        closeUploadFlow();
        await refreshAfterMutation();
      } else {
        const msg =
          response?.data?.message || "Could not upload files for this request";
        Toaster.error(msg);
        setUploadFlow((prev) => ({ ...prev, uploading: false }));
      }
    } catch (e) {
      Toaster.error(
        e?.response?.data?.message || "Error occurred while uploading files",
      );
      setUploadFlow((prev) => ({ ...prev, uploading: false }));
    }
  };

  const renderUploadedFilesCell = (row) => {
    const names = getUploadedFileNames(row);
    if (!names.length) {
      return <span className="requested-files-uploaded-empty">—</span>;
    }
    const label = names.join(", ");
    return (
      <span className="requested-files-uploaded-names" title={label}>
        {label}
      </span>
    );
  };

  return (
    <>
      {showRequestButton &&
        (inlineToolbar ? (
          <Button
            variant="outline"
            className="requested-files-request-btn"
            text="Request files"
            handleClick={openRequestModal}
          />
        ) : (
          <div
            className="requested-files-toolbar"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="outline"
              className="requested-files-request-btn"
              text="Request files"
              handleClick={openRequestModal}
            />
          </div>
        ))}

      {showTable && (
        <div className="requested-files-table-wrap">
          {showReminderColumns && rows.length > 0 ? (
            <div className="requested-files-bulk-toolbar">
              <span className="requested-files-bulk-count">
                {selectedRowIds.length
                  ? `${selectedRowIds.length} selected`
                  : "Select files to update reminder frequency"}
              </span>
              <Button
                variant="outline"
                className="requested-files-bulk-frequency-btn"
                text="Change frequency"
                disable={!selectedRowIds.length || bulkUpdating}
                handleClick={openBulkFrequencyModal}
              />
            </div>
          ) : null}
          {loading ? (
            <p className="requested-files-loading">Loading…</p>
          ) : rows.length === 0 ? (
            <Empty description="No requested files" />
          ) : (
            <table
              className={
                "documents-table requested-files-table" +
                (showReminderColumns
                  ? " requested-files-table--with-reminders"
                  : "")
              }
            >
              <thead>
                <tr>
                  {showReminderColumns ? (
                    <th
                      className="requested-files-select-head"
                      style={{ width: "44px" }}
                    >
                      <label className="requested-files-select-all">
                        <input
                          type="checkbox"
                          className="requested-files-checkbox"
                          checked={allRowsSelected}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate = someRowsSelected;
                            }
                          }}
                          onChange={(event) => {
                            event.stopPropagation();
                            toggleSelectAllRows();
                          }}
                          onClick={(event) => event.stopPropagation()}
                          aria-label="Select all requested files"
                        />
                      </label>
                    </th>
                  ) : null}
                  <th
                    className="requested-files-col-filename"
                    style={{
                      width: showBusinessNameColumn
                        ? showReminderColumns
                          ? "16%"
                          : "22%"
                        : showReminderColumns
                          ? "20%"
                          : "30%",
                    }}
                  >
                    File name
                  </th>
                  {showBusinessNameColumn ? (
                    <th
                      className="requested-files-col-business"
                      style={{ width: showReminderColumns ? "12%" : "16%" }}
                    >
                      Business
                    </th>
                  ) : null}
                  <th
                    className="requested-files-col-status"
                    style={{
                      width: showBusinessNameColumn
                        ? showReminderColumns
                          ? "9%"
                          : "12%"
                        : showReminderColumns
                          ? "10%"
                          : "16%",
                    }}
                  >
                    Status
                  </th>
                  {showReminderColumns ? (
                    <>
                      <th
                        className="requested-files-col-reminder-freq"
                        style={{ width: "10%" }}
                      >
                        Reminder
                      </th>
                      <th
                        className="requested-files-col-next-reminder"
                        style={{ width: "12%" }}
                      >
                        Next reminder
                      </th>
                    </>
                  ) : null}
                  <th
                    className="requested-files-col-requested"
                    style={{
                      width: showBusinessNameColumn
                        ? showReminderColumns
                          ? "12%"
                          : "16%"
                        : showReminderColumns
                          ? "14%"
                          : "22%",
                    }}
                  >
                    Requested
                  </th>
                  <th
                    className="requested-files-col-uploaded"
                    style={{
                      width: showBusinessNameColumn
                        ? showReminderColumns
                          ? "14%"
                          : "18%"
                        : showReminderColumns
                          ? "16%"
                          : "22%",
                    }}
                  >
                    Uploaded files
                  </th>
                  <th
                    style={{ width: "10%", textAlign: "right" }}
                    className="requested-files-action-head"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const rid = getRowId(row);
                  const selectionId = getRowSelectionId(row, rowIndex);
                  const st = String(row.status || "pending").toLowerCase();
                  const busy = updatingId === rid;
                  const isCompleted = st === "completed";
                  const uploadedNames = getUploadedFileNames(row);
                  const hasUploads = uploadedNames.length > 0;
                  const canClientUpload = isClient && !isCompleted;
                  const isSelected = selectedRowIds.includes(selectionId);
                  return (
                    <tr
                      key={selectionId}
                      className={
                        "documents-row" +
                        (onRowClick ? " requested-files-row--clickable" : "")
                      }
                      onClick={
                        onRowClick
                          ? (event) => {
                              if (
                                event.target.closest(
                                  ".requested-files-select-cell",
                                )
                              ) {
                                return;
                              }
                              onRowClick(row);
                            }
                          : undefined
                      }
                    >
                      {showReminderColumns ? (
                        <td className="requested-files-select-cell">
                          {!isCompleted ? (
                            <label className="requested-files-row-select">
                              <input
                                type="checkbox"
                                className="requested-files-checkbox"
                                checked={isSelected}
                                onChange={(event) => {
                                  event.stopPropagation();
                                  setRowSelection(
                                    selectionId,
                                    event.target.checked,
                                  );
                                }}
                                onClick={(event) => event.stopPropagation()}
                                aria-label={`Select ${fileNameCell(row)}`}
                              />
                            </label>
                          ) : null}
                        </td>
                      ) : null}
                      <td className="requested-files-filename-cell">
                        <span>{fileNameCell(row)}</span>
                        {Array.isArray(row.staff_attachments) &&
                        row.staff_attachments.length > 0 ? (
                          <div className="requested-files-staff-docs">
                            {row.staff_attachments.map((attachment) => (
                              <div
                                key={`${getRowId(row)}-${attachment.index}`}
                                className="requested-files-staff-doc-row"
                              >
                                <span>{attachment.name}</span>
                                <button
                                  type="button"
                                  className="requested-files-staff-doc"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    previewStaffAttachment(row, attachment);
                                  }}
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  className="requested-files-staff-doc"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    downloadStaffAttachment(row, attachment);
                                  }}
                                >
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </td>
                      {showBusinessNameColumn ? (
                        <td className="requested-files-business-cell">
                          {getRequestedFileCategory(row, categoryFilter) ===
                          "business"
                            ? getRequestedFileBusinessName(
                                row,
                                categoryFilter,
                                clientBusinesses,
                              ) || "—"
                            : "—"}
                        </td>
                      ) : null}
                      <td>
                        <span
                          className={
                            "requested-files-status-badge" +
                            (isCompleted
                              ? " requested-files-status-badge--completed"
                              : " requested-files-status-badge--pending")
                          }
                        >
                          {isCompleted ? "Completed" : "Pending"}
                        </span>
                      </td>
                      {showReminderColumns ? (
                        <>
                          <td className="requested-files-reminder-cell">
                            <span
                              title={getReminderProgressTitle(row) || undefined}
                            >
                              {formatReminderFrequencyLabel(row)}
                            </span>
                          </td>
                          <td className="requested-files-next-reminder-cell">
                            <span
                              title={getReminderProgressTitle(row) || undefined}
                            >
                              {formatNextReminderLabel(row, dateFormatter)}
                            </span>
                          </td>
                        </>
                      ) : null}
                      <td>
                        {dateTimeFormatter(
                          row.createdAt || row.created_at || row.requested_at,
                        )}
                      </td>
                      <td className="requested-files-uploaded-cell">
                        {renderUploadedFilesCell(row)}
                      </td>
                      <td
                        className="requested-files-action-cell"
                        onClick={(e) => e.stopPropagation()}
                        style={{ textAlign: "right" }}
                      >
                        {isClient ? (
                          canClientUpload ? (
                            <Button
                              variant="outline"
                              text={hasUploads ? "Upload" : "Upload"}
                              disable={busy}
                              handleClick={() => openUploadFlow(row)}
                            />
                          ) : null
                        ) : (
                          <IconButton
                            size="small"
                            aria-label="Change status"
                            aria-haspopup="true"
                            disabled={busy}
                            onClick={(e) => openStatusMenu(e, row)}
                            className="requested-files-action-btn"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!isClient && statusMenu.anchor && (
        <Menu
          anchorEl={statusMenu.anchor}
          open
          onClose={closeStatusMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          disableScrollLock
        >
          <MenuItem
            dense
            onClick={() => {
              const row = statusMenu.row;
              closeStatusMenu();
              if (row) {
                setEditingRow(row);
                setReminderFrequencyDays(
                  String(getReminderFrequencyDays(row) || ""),
                );
                setRequestFileRows([
                  { key: `req-edit-${Date.now()}`, value: fileNameCell(row) },
                ]);
                setModalOpen(true);
              }
            }}
          >
            <ListItemText primary="Edit" />
          </MenuItem>
          <MenuItem
            dense
            disabled={
              String(statusMenu.row?.status || "pending").toLowerCase() ===
              "pending"
            }
            onClick={() => pickStatus("pending")}
          >
            <ListItemText primary="Mark as pending" />
          </MenuItem>
          <MenuItem
            dense
            disabled={
              String(statusMenu.row?.status || "").toLowerCase() === "completed"
            }
            onClick={() => pickStatus("completed")}
          >
            <ListItemText primary="Mark as completed" />
          </MenuItem>
        </Menu>
      )}

      {uploadFlow.open && (
        <Modal
          title="Upload files for request"
          open
          handleClose={closeUploadFlow}
          className="timesheet-view-modal upload-requested-file-modal"
        >
          <div className="requested-upload-modal">
            <div className="requested-upload-top">
              <div className="requested-upload-meta">
                <div className="requested-upload-label">Requested file</div>
                <div className="requested-upload-name">
                  {fileNameCell(uploadFlow.row || {})}
                </div>
              </div>
            </div>

            <div className="requested-upload-picker">
              <div className="requested-upload-picker-head">
                <div className="requested-upload-picker-title">
                  Select folder to upload into
                </div>
              </div>

              <div className="documents-breadcrumb-row requested-upload-breadcrumbs">
                {uploadFlow.navPath.map((item, idx) => (
                  <React.Fragment key={`req-crumb-${idx}`}>
                    {idx > 0 && (
                      <span className="documents-location-separator">›</span>
                    )}
                    <span
                      className={
                        "documents-location-item" +
                        (idx === uploadFlow.navPath.length - 1
                          ? " current-folder"
                          : "")
                      }
                      onClick={() => goToCrumbInUploadPicker(idx)}
                    >
                      {item.name}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              {uploadFlow.loading ? (
                <p className="requested-files-loading">Loading folders…</p>
              ) : uploadFlow.folders.length === 0 ? (
                <Empty description="No folders in this location" />
              ) : (
                <div className="requested-upload-folder-list">
                  {uploadFlow.folders.map((f) => (
                    <button
                      key={f.client_folder_id}
                      type="button"
                      className="requested-upload-folder-row"
                      onClick={() => openFolderInUploadPicker(f)}
                    >
                      <span className="requested-upload-folder-name">
                        {f.folder_name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="requested-upload-filepick">
              {!hasSelectedUploadFolder ? (
                <div className="requested-upload-filepick-hint">
                  <p>
                    Please select a folder to upload your files into or{" "}
                    <span
                      onClick={() => setAddFolderOpen(true)}
                      style={{
                        color: "#3B82F6",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      click here
                    </span>{" "}
                    to add a new folder.
                  </p>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    id="requested-file-input"
                    multiple
                    accept={DOCUMENT_UPLOAD_ACCEPT}
                    onChange={onPickFiles}
                    style={{ display: "none" }}
                  />
                  <div className="requested-upload-filepick-row">
                    <label
                      htmlFor="requested-file-input"
                      className="requested-upload-filepick-btn"
                    >
                      Choose files
                    </label>
                    <div className="requested-upload-filepick-hint">
                      {DOCUMENT_UPLOAD_SIZE_NOTE} Select multiple files, then
                      upload once to: <span>{getUploadKeyPath()}</span>
                    </div>
                  </div>

                  {uploadFlow.selectedFiles.length > 0 && (
                    <div className="requested-upload-selected">
                      {uploadFlow.selectedFiles.map((f, idx) => (
                        <div
                          key={`${f.name}-${idx}`}
                          className="requested-upload-selected-row"
                        >
                          <span className="requested-upload-selected-name">
                            {f.name}
                          </span>
                          <button
                            type="button"
                            className="requested-upload-selected-remove"
                            onClick={() => removePickedFile(idx)}
                            disabled={uploadFlow.uploading}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer" style={{ gap: "8px", width: "100%" }}>
              <Button
                variant="outline"
                text="Cancel"
                disable={uploadFlow.uploading}
                handleClick={closeUploadFlow}
              />
              <Button
                text={uploadFlow.uploading ? "Uploading…" : "Upload"}
                disable={
                  uploadFlow.uploading ||
                  !hasSelectedUploadFolder ||
                  !uploadFlow.fileData.length
                }
                handleClick={submitUploadForRequest}
              />
            </div>
          </div>
        </Modal>
      )}

      {bulkFrequencyModalOpen && (
        <Modal
          title="Change reminder frequency"
          open
          handleClose={closeBulkFrequencyModal}
          className="timesheet-view-modal requested-files-bulk-frequency-modal"
        >
          <div className="requested-files-modal-body">
            <p className="requested-files-modal-hint">
              Update reminder frequency for{" "}
              <strong>
                {selectedRows.length} file
                {selectedRows.length === 1 ? "" : "s"}
              </strong>
              . Changing frequency restarts the reminder schedule for each
              selected file.
            </p>
            <TextField
              fullWidth
              type="number"
              label="Reminder frequency (days)"
              value={bulkReminderFrequencyDays}
              onChange={(e) => {
                const value = String(e.target.value || "");
                setBulkReminderFrequencyDays(value.replace(/[^0-9]/g, ""));
              }}
              margin="dense"
              variant="outlined"
              placeholder="e.g. 7"
              inputProps={{ min: 1 }}
              helperText={
                bulkReminderFrequencyDays
                  ? "Reminders: now, +" +
                    bulkReminderFrequencyDays +
                    " days, +" +
                    Number(bulkReminderFrequencyDays) * 2 +
                    " days, +" +
                    Number(bulkReminderFrequencyDays) * 3 +
                    " days, +" +
                    Number(bulkReminderFrequencyDays) * 4 +
                    " days (5 total)"
                  : "Default is 7 days. Change if needed — send now, then 4 reminders at this interval"
              }
            />
            <div className="modal-footer" style={{ gap: "8px" }}>
              <Button
                variant="outline"
                text="Cancel"
                disable={bulkUpdating}
                handleClick={closeBulkFrequencyModal}
              />
              <Button
                text={
                  bulkUpdating
                    ? "Updating…"
                    : `Update ${selectedRows.length} file${
                        selectedRows.length === 1 ? "" : "s"
                      }`
                }
                disable={bulkUpdating}
                handleClick={submitBulkFrequencyChange}
              />
            </div>
          </div>
        </Modal>
      )}

      {modalOpen && (
        <Modal
          title="Request files from client"
          open
          handleClose={closeRequestModal}
          className="timesheet-view-modal requested-files-request-modal"
        >
          <div className="requested-files-modal-body">
            <p className="requested-files-modal-hint">
              Add one or more file names for{" "}
              <strong>
                {categoryFilter === "business" ? "Business" : "Individual"}
              </strong>{" "}
              documents. The client will be notified for each request. You can
              also attach documents for the client to download, or add a saved
              template for this document type. Attaching documents is optional.
            </p>
            <TextField
              fullWidth
              type="number"
              label="Reminder frequency (days)"
              value={reminderFrequencyDays}
              onChange={(e) => {
                const value = String(e.target.value || "");
                setReminderFrequencyDays(value.replace(/[^0-9]/g, ""));
              }}
              margin="dense"
              variant="outlined"
              placeholder="e.g. 7"
              inputProps={{ min: 1 }}
              helperText={
                reminderFrequencyDays
                  ? "Reminders: now, +" +
                    reminderFrequencyDays +
                    " days, +" +
                    Number(reminderFrequencyDays) * 2 +
                    " days, +" +
                    Number(reminderFrequencyDays) * 3 +
                    " days, +" +
                    Number(reminderFrequencyDays) * 4 +
                    " days (5 total)"
                  : "Default is 7 days. Change if needed — send now, then 4 reminders at this interval"
              }
            />
            <div className="requested-files-request-list">
              {requestFileRows.map((row, index) => (
                <div key={row.key} className="requested-files-request-row">
                  <div className="requested-files-request-fields">
                    <TextField
                      fullWidth
                      label={index === 0 ? "File name" : `File name ${index + 1}`}
                      value={row.value}
                      onChange={(e) =>
                        updateRequestFileRow(row.key, e.target.value)
                      }
                      margin="dense"
                      variant="outlined"
                      placeholder="Enter the name of the file you need"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !submitting) {
                          e.preventDefault();
                          submitRequest();
                        }
                      }}
                    />
                    {!editingRow ? (
                      <div className="requested-files-attach">
                        <TextField
                          className="requested-files-template"
                          select
                          fullWidth
                          label="Add template (optional)"
                          value={row.templateId || ""}
                          disabled={submitting}
                          margin="dense"
                          variant="outlined"
                          onChange={(event) =>
                            updateRequestFileTemplate(
                              row.key,
                              event.target.value,
                            )
                          }
                          SelectProps={{
                            MenuProps: {
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                            },
                          }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {documentTemplates.map((template) => (
                            <MenuItem
                              key={template.template_id}
                              value={template.template_id}
                            >
                              {template.template_name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <label className="requested-files-attach-btn">
                          Attach documents (optional)
                          <input
                            type="file"
                            multiple
                            accept={DOCUMENT_UPLOAD_ACCEPT}
                            disabled={submitting}
                            onChange={(event) => {
                              addRequestFileAttachments(
                                row.key,
                                event.target.files,
                              );
                              event.target.value = "";
                            }}
                          />
                        </label>
                        <span className="requested-files-attach-note">
                          {DOCUMENT_UPLOAD_SIZE_NOTE}
                        </span>
                        {(row.files || []).length > 0 ? (
                          <ul className="requested-files-attach-list">
                            {row.files.map((file, fileIndex) => (
                              <li key={`${file.name}-${file.size}-${fileIndex}`}>
                                <span>{file.name}</span>
                                <button
                                  type="button"
                                  disabled={submitting}
                                  onClick={() =>
                                    removeRequestFileAttachment(row.key, fileIndex)
                                  }
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Remove file name"
                    className="requested-files-request-remove"
                    onClick={() => removeRequestFileRow(row.key)}
                    disabled={submitting}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="requested-files-request-add"
              onClick={addRequestFileRow}
              disabled={submitting}
            >
              <AddCircleOutlineIcon fontSize="small" />
              Add another file
            </button>
            <div className="modal-footer" style={{ gap: "8px" }}>
              <Button
                variant="outline"
                text="Cancel"
                disable={submitting}
                handleClick={closeRequestModal}
              />
              <Button
                text={
                  submitting
                    ? "Sending…"
                    : `Send request${requestFileRows.length > 1 ? "s" : ""}`
                }
                disable={submitting}
                handleClick={submitRequest}
              />
            </div>
          </div>
        </Modal>
      )}

      {addFolderOpen && (
        <Modal
          title={null}
          open
          handleClose={() => {
            setAddFolderOpen(false);
            setNewFolderName("");
          }}
          className="timesheet-view-modal add-folder-modal"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div>
              <h3 style={{ fontWeight: "bold", fontSize: "24px" }}>
                {uploadFlow.navPath.length === 1 &&
                uploadFlow.navPath[0]?.id === 0
                  ? "Add Folder"
                  : "Add Sub Folder"}
              </h3>
            </div>
            <button
              className="close-btn"
              onClick={() => {
                setAddFolderOpen(false);
                setNewFolderName("");
              }}
            >
              ✕
            </button>
          </div>

          <div className="add_folder_modal">
            <div
              style={{ width: "100%", marginTop: "7px", marginBottom: "16px" }}
            >
              {uploadFlow.navPath.length === 1 &&
              uploadFlow.navPath[0]?.id === 0 ? (
                <Labelbox
                  type="select"
                  labelname="Folder Name"
                  dropdown={getRootUploadFolderDropdownOptions(
                    getRequestedFileCategory(uploadFlow.row, categoryFilter),
                  )}
                  changeData={(data) => setNewFolderName(data)}
                  value={newFolderName}
                />
              ) : (
                <Labelbox
                  type="text"
                  labelname="Folder Name"
                  changeData={(data) =>
                    setNewFolderName(data.replace(/[^\w ]/g, ""))
                  }
                  value={newFolderName}
                  placeholder="Enter name"
                  onKeyEnter={() => {
                    if (!creatingFolder) {
                      handleCreateFolder();
                    }
                  }}
                />
              )}
            </div>
            <div className="modal-actions-right">
              <button
                className="upload-button"
                disabled={creatingFolder || !newFolderName.trim()}
                onClick={handleCreateFolder}
              >
                {creatingFolder ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default RequestedFilesBlock;
